import Alerts from 'lib/alerts';
import Log from 'lib/log';
import localStorageUtil from 'helpers/localStorageUtil';
import BaseRouter from './Base.js';
import ListView from '../views/EmployeesManage.js';
import LayoutView from '../views/employee/Layout.js';
import PayslipView from '../views/employee/Payslip.js';
import BulkUploadView from '../views/EmployeesBulkUpload.js';
import BulkUploadPayslipsView from '../views/EmployeesPayslipsBulkUpload.js';

import Address from 'newModels/address.js';
import Employee from 'newModels/employee.js';
import Payroll from 'newModels/payroll.js';
import Payslip from 'newModels/payslip.js';
import Employees from 'newCollections/employees.js';
import Positions from 'newCollections/positions.js';
import DefaultPayrollDetails from 'newCollections/default-payroll-details.js';
import CompanyLocations from 'newCollections/company-locations.js';
import LeaveTypes from 'newCollections/leave-types.js';
import Payslips from 'newCollections/payslips.js';
import ItemYearlyTotals from 'newCollections/item-yearly-totals.js';
import Attachments from 'newCollections/attachments.js';
import PayslipDetails from 'newCollections/payslip-details.js';
import ActionLogs from 'newCollections/action-logs.js';
import BulkUploads from 'newCollections/bulk-uploads.js';
import LeaveRequests from 'newCollections/leave-requests.js';
import LeaveApprovals from 'newCollections/leave-approvals.js';
import moment from 'moment';

const log = Log('employer/employee-router');

module.exports = BaseRouter.extend({
    routes: {
        'employees':                                  'list',
        'employees/bulk-upload':                      'bulkUpload',
        'employees/bulk-upload-payslips':             'bulkUploadPayslips',
        'employees/:status':                          'list',
        'employees/:status/:sort/:order':             'list',
        'employees/:status/:sort/:order/:searchTerm': 'list',
        'employee':                                   'add',
        'employee/add':                               'add',
        'employee/:id':                               'personalInfo',
        'employee/:id/edit':                          'personalInfo',
        'employee/:id/personalInfo':                  'personalInfo',
        'employee/:id/personalInfo/:isNew':           'personalInfoNew',
        'employee/:id/employmentDetails':             'employmentDetails',
        'employee/:id/employmentDetails/:isNew':      'employmentDetails',
        'employee/:id/statutoryRequirements':         'statutoryRequirements',
        'employee/:id/statutoryRequirements/:isNew':  'statutoryRequirements',
        'employee/:id/previousPayslips':              'previousPayslips',
        'employee/:id/previousPayslips/:isNew':       'previousPayslips',
        'employee/:id/actionLog':                     'actionLog',
        'employee/:id/actionLog/:isNew':              'actionLog',
        'employee/:id/payslip/:payslipId':            'payslip'
    },

    titles: {
        'employees':                                  'Manage Employees',
        'employees/bulk-upload':                      'Employees bulk upload',
        'employees/:status':                          'Manage Employees',
        'employees/:status/:sort/:order':             'Manage Employees',
        'employees/:status/:sort/:order/:searchTerm': 'Manage Employees',
        'employee':                                   'Add Employee',
        'employee/add':                               'Add Employee',
        'employee/:id':                               'Edit Personal Info',
        'employee/:id/edit':                          'Edit Personal Info',
        'employee/:id/personalInfo':                  'Edit Personal Info',
        'employee/:id/personalInfo/:isNew':           'Edit Personal Info',
        'employee/:id/employmentDetails':             'Edit Employment Details',
        'employee/:id/employmentDetails/:isNew':      'Edit Employment Details',
        'employee/:id/statutoryRequirements':         'Edit Statutory ' +
                                                      'Requirements',
        'employee/:id/statutoryRequirements/:isNew':  'Edit Statutory ' +
                                                      'Requirements',
        'employee/:id/previousPayslips':              'Edit Previous Payslips',
        'employee/:id/previousPayslips/:isNew':       'Edit Previous Payslips',
        'employee/:id/actionLog':                     'Action Log',
        'employee/:id/actionLog/:isNew':              'Action Log',
        'employee/:id/payslip/:payslipId':            'Payslip'
    },

    initialize: function(options) {
        this.options = _.defaults(options || {}, {log: log});
        BaseRouter.prototype.initialize.call(this, this.options);

        this.layout = new LayoutView({
            el: this.el
        });
    },

    list: function(status, sortKey, sortOrder, searchTerm) {
        const employees = new Employees(null, {
            company: app.company,
            sortKey: sortKey || 'canonicalFullName',
            sortOrder: sortOrder || 'ascending',
            pagination: {rowsPerPage: 25},
            searchString: searchTerm || '',
            filter: {
                where: {
                    hiddenAt: (status || 'active') === 'active' ?
                        null :
                        {neq: null}
                }
            }
        });

        this.view = app.contentView = new ListView({
            collection: employees,
            el: this.el,
            archived: (status === 'archived')
        });
    },

    add: function() {
        return app.company.fetch()
            .then(() => {
                if (app.company.isAllowAddEmployees()) {
                    localStorageUtil.removeItem('ytdYear');
                    this.layout.renderTab('personalInfo');
                    this.layout.renderContent({
                        isNew: true,
                        model: new Employee({
                            companyId: app.company.id,
                            company: app.company,
                            address: new Address()
                        })
                    });
                    this.layout.hideLoader();
                } else {
                    Alerts.show('warning', {
                        text: `You have reached your maximum number of active \
                            employees. Please reach out to our customer \
                            support if you would like to add more employee(s)`,
                        confirmButtonText: 'OK',
                        callback: () => {
                            window.history.back();
                        }
                    });
                }
            });
    },

    fetchEmployeeById: function(id) {
        return new Employee({objectId: id})
            .fetch({validate: false})
            .catch(err => {
                if (_.isObject(err) && err.response.error.statusCode === 401) {
                    Alerts.show('error', {
                        text: 'Employee not found.',
                        callback: () =>
                            app.mainRouter.navigate(
                                'employees', {trigger: true}
                            )
                    });

                    return Promise.resolve(false);
                }
            });
    },

    personalInfo: function(id, isNew) {
        this.layout.renderTab('personalInfo');

        return this.fetchEmployeeById(id)
            .then(employee => {
                if (!employee) {
                    return;
                }

                return Promise.all([
                    employee.fetchAddress(),
                    employee.fetchBankAccount()
                ]).then(() => Promise.resolve(employee));
            })
            .then(employee => {
                return app.company.fetchCurrentPayroll()
                    .then(payroll => {
                        this.layout.renderContent({
                            model: employee,
                            unsubmittedPayroll: payroll,
                            isNew: !!isNew
                        });
                    });
            }).catch(
                err => { Alerts.show('criticalError', err); }
            ).finally(
                () => this.layout.hideLoader()
            );
    },

    employmentDetails: function(id, isNew) {
        this.layout.renderTab('employmentDetails');

        const positions = new Positions(null, {
            company: app.company,
            pagination: false
        });
        const approverEmployees = new Employees(null, {
            company: app.company,
            pagination: false
        });
        const locations = new CompanyLocations(null, {
            company: app.company,
            pagination: false
        });
        const leaveTypes = new LeaveTypes(null, {
            company: app.company,
            pagination: false
        });
        return this.fetchEmployeeById(id).then(employee => {
            if (employee) {
                const defaultPayrollDetails = new DefaultPayrollDetails(null, {
                    employee: employee, syncRemove: true, pagination: false
                });
                const leaveApprovals = new LeaveApprovals(null, {
                    requester: employee, syncRemove: true, pagination: false
                });

                employee.company = app.company;

                return Promise.all([
                    Promise.resolve(employee),
                    app.company.fetchCurrentPayroll(),
                    LeaveTypes.fetchCarryForwardDays(employee),
                    defaultPayrollDetails.fetch({
                        filter: {
                            where: {hiddenAt: null},
                            limit: 9999
                        }
                    }).then(() => defaultPayrollDetails),
                    leaveApprovals.fetch({
                        filter: {
                            limit: 9999
                        }
                    }).then(() => leaveApprovals),
                    positions.fetch({filter: {
                        limit: 9999,
                        where: {hiddenAt: null}
                    }}),
                    approverEmployees.fetch({filter: {
                        where: {
                            hiddenAt: null,
                            userId: {neq: null}
                        },
                        limit: 9999
                    }, validate: false}),
                    locations.fetch({filter: {
                        where: {hiddenAt: null},
                        limit: 9999
                    }}),
                    leaveTypes.fetch({filter: {
                        limit: 9999
                    }}).then(() => {
                        const typeIds = leaveTypes.map(type => type.id);
                        return LeaveRequests
                            .fetchForTypes({
                                typeIds,
                                employeeIds: [employee.id]
                            })
                            .then(employeeRequests => {
                                const requestedType = employeeRequests.map(
                                    req => req.get('typeId')
                                );
                                const enabledType = leaveTypes.filter(
                                    type => !type.get('hiddenAt')
                                ).map(type => type.id);
                                return leaveTypes.filter(type =>
                                    requestedType.includes(type.id) ||
                                    enabledType.includes(type.id)
                                );
                            }).then(() => leaveTypes.fetchBalances(
                                employee,
                                moment().tz('Asia/Kuala_Lumpur')
                                    .endOf('day').toDate()
                            ));
                    }),
                    employee.fetchPosition()
                ]);
            } else {
                return false;
            }
        }).then(([
            employee,
            unsubmittedPayroll,
            carryForwardDays,
            defaultPayrollDetails,
            leaveApprovals
        ]) => {
            if (!employee) {
                return;
            }

            const payrollItems = app.payrollItems.getSelectableDefaults();
            if (!employee.get('companyLocationId')) {
                employee.set(
                    {companyLocationId: locations.getPrimary().id},
                    {silent: true}
                );
                employee.locationAdded = true;
            }

            const typeIds = leaveTypes.map(type => type.id);
            return LeaveRequests
                .fetchForTypes({typeIds, employeeIds: [employee.id]})
                .then(employeeRequests => {
                    const requestedType = employeeRequests.map(
                        req => req.get('typeId')
                    );
                    const enabledType = leaveTypes.filter(
                        type => !type.get('hiddenAt')
                    ).map(type => type.id);
                    const types = new LeaveTypes(
                        leaveTypes.filter(type =>
                            requestedType.includes(type.id) ||
                            enabledType.includes(type.id)
                        ),
                        {company: app.company, pagination: false}
                    );

                    return this.layout.renderContent({
                        isNew: !!isNew,
                        model: employee,
                        positions: positions,
                        approverEmployees: approverEmployees,
                        locations: locations,
                        payrollItems: payrollItems,
                        payrollDetails: defaultPayrollDetails,
                        unsubmittedPayroll: unsubmittedPayroll,
                        leaveTypes: types,
                        carryForwardDays: carryForwardDays,
                        leaveApprovals: leaveApprovals
                    });
                });
        }).catch(err => {
            Alerts.show('criticalError', err);
        }).finally(
            () => this.layout.hideLoader()
        );
    },

    statutoryRequirements: function(id, isNew) {
        this.layout.renderTab('statutoryRequirements');

        this.fetchEmployeeById(id)
            .then(employee => {
                if (!employee) {
                    return;
                }

                const defaultPayrollDetails = new DefaultPayrollDetails(null, {
                    employee: employee, syncRemove: true, pagination: false
                });

                return Promise.all([
                    app.company.fetchCurrentPayroll(),
                    defaultPayrollDetails.fetch({
                        filter: {
                            where: {hiddenAt: null},
                            limit: 9999
                        }
                    }).then(() => defaultPayrollDetails),
                ]).then(([unsubmittedPayroll, defaultPayrollDetails,]) => {
                    this.layout.renderContent({
                        isNew: !!isNew,
                        model: employee,
                        unsubmittedPayroll: unsubmittedPayroll,
                        defaultPayrollDetails: defaultPayrollDetails
                    });
                })
            })

            .catch(err => {
                Alerts.show('criticalError', err);
            }).finally(
                () => this.layout.hideLoader()
            );
    },

    previousPayslips: function(id, isNew) {
        this.layout.renderTab('previousPayslips');
        const ytdYear = parseInt(localStorageUtil.getItem('ytdYear')) ||
            Payroll.getCurrentPeriodDate().getFullYear();

        return this.fetchEmployeeById(id).then(employee => {
            if (!employee) {
                return false;
            }

            const payslips = new Payslips(null, {
                employee: employee,
                sortKey: 'createdAt',
                sortOrder: 'descending',
                pagination: false
            });
            return Promise.all([
                Promise.resolve(employee),
                employee.get('hasPayslips') ?
                    payslips.fetch({filter: {
                        where: {
                            status: 'done'
                        },
                        limit: 9999
                    }}).then(() => payslips) :
                    Promise.resolve([]),
                new ItemYearlyTotals(null, {
                    sortKey: 'createdAt',
                    pagination: false,
                    employee,
                    year: ytdYear
                }).fetchForPreviousPayslips(),
                app.company.fetchCurrentPayroll()
            ]);
        }).then((
            [employee, payslips, yearlyTotals, unsubmittedPayroll]
        ) => {
            const payrollItems =
                app.payrollItems.getSelectablePreviousPayslips();
            const attachments = {};

            if (!employee) {
                return;
            }

            return Promise.all(payslips.map(payslip => {
                return new Attachments(null, {
                    attachable: payslip,
                    pagination: false
                })
                    .fetch({filter: {limit: 9999}})
                    .then(r => attachments[payslip.id] = r);
            })).then(() => {
                this.layout.renderContent({
                    isNew: !!isNew,
                    model: employee,
                    payrollItems: payrollItems,
                    yearlyTotals: yearlyTotals,
                    payslips: payslips,
                    payslipAttachments: attachments,
                    year: ytdYear,
                    unsubmittedPayroll
                });
            });
        }).catch(err => {
            log.error(err);
            Alerts.show('criticalError', err);
        }).finally(
            () => this.layout.hideLoader()
        );
    },

    actionLog: function(id) {
        this.layout.renderTab('actionLog');

        return this.fetchEmployeeById(id).then(employee => {
            if (!employee) {
                return false;
            }

            return Promise.all([
                Promise.resolve(employee),
                new ActionLogs().fetchForEmployee(employee)
            ]);
        }).then(([employee, actionLogs]) => {
            this.layout.renderContent({
                model: employee,
                collection: actionLogs,
                fetchMore: options => {
                    return new ActionLogs()
                        .fetchForEmployee(employee, options);
                }
            });
        }).finally(() => this.layout.hideLoader());
    },

    // TODO it is a big and multiplied method, refactor it later
    fetchBalance: function(payslip) {
        const fetchYearlyBalances = () => {
            return app.leaveTypes.fetchBalances(
                payslip.getEmployee(),
                payslip.getPayroll().getPeriod().endOf('month').toDate()
            );
        };

        const transformYearly = leaveTypes => {
            return leaveTypes.map(leaveType => {
                const name = leaveType.get('name');
                const id = leaveType.id;
                const entitled = leaveType.getEntitled() || 0;
                const taken = leaveType.getTaken() || 0;
                const remaining = Math.max(0, entitled - taken);
                return {
                    id,
                    name,
                    entitled,
                    remaining,
                    taken
                };
            });
        };

        const mergeBalances = (yearly, monthly) => {
            const obligated = _.filter(yearly, balance => {
                return [
                    'Medical Leave',
                    'Annual Leave',
                    'Unpaid Leave',
                ].includes(balance.name);
            });
            const yearlyHasInMontly = _.filter(yearly, balance => (
                _.map(monthly, t => t.id)
                    .includes(balance.id) && balance.taken > 0
            ))
            .concat(obligated);

            const mergeBalances = _.map(yearlyHasInMontly, balance => {
                const sameMonthlyBalance = monthly
                    .find(t => t.id === balance.id);
                if (balance.taken > 0) {
                    const newTaken = sameMonthlyBalance ?
                        sameMonthlyBalance.taken : 0;
                    balance.taken = newTaken;
                }
                return balance;
            });
            return mergeBalances;
        };

        const fetchMonthlyBalance = (types, reference) => {
            const end = moment(reference).endOf('month').toDate();
            const start = moment(reference).startOf('month').toDate();
            return LeaveRequests.fetchForBalances({
                employee: payslip.getEmployee(),
                typeIds: types.map(t => t.id),
                start,
                end
            }).then((requests) => {
                const simplified = requests.map(request => {
                    const id = request.get('typeId');
                    const taken = request.get('daysRequired');
                    return {
                        id,
                        taken
                    };
                }) || [];
                const groupedById = _.groupBy(simplified, 'id');
                const summedById = _.map(groupedById, requests => {
                    const taken = _.reduce(requests,
                        (sum, request) => sum + request.taken, 0
                    );
                    return {
                        id: requests[0].id,
                        taken
                    };
                });

                return summedById;
            });
        };
        return fetchYearlyBalances()
            .then(yearlyBalances => Promise.all([
                transformYearly(yearlyBalances),
                fetchMonthlyBalance(
                    yearlyBalances,
                    payslip.getPayroll().get('periodDate')
                )
            ]))
            .then(([yearly, monthly]) => mergeBalances(yearly, monthly));
    },

    payslip: function(id, payslipId) {
        this.undelegateEvents();

        return new Payslip({objectId: payslipId})
            .fetch({validate: false})
            .then(payslip => {
                if (!payslip) {
                    Alerts.show('error', {
                        text: 'Payslip not found.',
                        callback: () =>
                            app.mainRouter.navigate(
                                'employee/' + id + '/previousPayslips',
                                {trigger: true}
                            )
                    });

                    return Promise.resolve(false);
                }
                const details = new PayslipDetails(null, {
                    payslip: payslip,
                    pagination: false
                });
                return Promise.all([
                    Promise.resolve(payslip),
                    details.fetch({filter: {limit: 9999}}).then(() => details),
                    payslip.fetchCompany().then(company => {
                        return company.fetchPrimaryLocation();
                    }).then(companyLocation => {
                        return companyLocation.fetchAddress()
                            .then(() => companyLocation);
                    }),
                    payslip.fetchEmployee()
                        .then(() => payslip.fetchPayroll())
                        .then(() => this.fetchBalance(payslip))
                ]);
            }).then(([payslip, details, companyLocation, balances]) => {
                details.balances = balances;
                this.payslipView = new PayslipView({
                    model: payslip,
                    payslipDetails: details,
                    companyLocation: companyLocation
                });

                $(this.el).html('').append(this.payslipView.el);
            }).catch(err => {
                Alerts.show('criticalError', err);
            });
    },

    bulkUpload: function() {
        return app.company.fetch()
            .then(() => {
                if (app.company.isAllowAddEmployees()) {
                    this.layout.$('.js-loading-tab').addClass('active');
                    return new BulkUploads(null, {company: app.company})
                        .fetch({where: {status: {neq: 'done'}}})
                        .then(pendingUploads => {
                            let view = new BulkUploadView({
                                model: pendingUploads.at(0)
                            });
                            $(this.el).html(view.render().el);
                        }).catch(log.error)
                        .finally(() => this.layout.hideLoader());
                } else {
                    Alerts.show('warning', {
                        text: `You have reached your maximum number of active \
                            employees. Please reach out to our customer \
                            support if you would like to add more employee(s)`,
                        confirmButtonText: 'OK',
                        callback: () => {
                            window.history.back();
                        }
                    });
                }
            });
    },

    bulkUploadPayslips: function() {
        this.layout.$('.js-loading-tab').addClass('active');
        return new BulkUploads(null, {company: app.company})
            .fetch({where: {status: {neq: 'done'}}})
            .then(pendingUploads => {
                let view = new BulkUploadPayslipsView({
                    model: pendingUploads.at(0)
                });
                $(this.el).html(view.render().el);
            }).catch(log.error)
            .finally(() => this.layout.hideLoader());
    }
});



// WEBPACK FOOTER //
// ./app/employer/routers/Employee.js